import { useMemo } from 'react';
import { useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
	LWApiPublicNotificationCurrentParamsRawI,
	LWApiPublicNotificationCurrentResultRawI,
	LWApiPublicNotificationDetailsResultRawI,
} from '@liquidweb-dev/club-sauce/public/notifications/index.raw';
import type { ApiErrorI } from '@liquidweb-dev/club-sauce/types/apiError';
import dayjs from 'dayjs';
import api from 'modules/queries/api';

export const isProvisioningSystem = (system: string) => {
	return system.toLowerCase().startsWith('provisioning');
};

export const isNocworxWorkflowSystem = (system: string) => {
	return system.toLowerCase() === 'nocworx workflow';
};

export const isBareMetalSystem = (system: string) => {
	return system.toLowerCase() === 'bare metal';
};

export const NOTIFICATIONS_CURRENT_QUERY_KEY = '/notifications/current';

const useNotificationsCurrent = (
	params: LWApiPublicNotificationCurrentParamsRawI,
	options?: UseQueryOptions<
		LWApiPublicNotificationCurrentResultRawI,
		ApiErrorI
	>,
) =>
	useQuery<LWApiPublicNotificationCurrentResultRawI, ApiErrorI>({
		queryKey: [NOTIFICATIONS_CURRENT_QUERY_KEY, params],
		queryFn: () => api.notifications.apiRaw.current(params),
		...options,
	});

export const useLatestNotifications = (
	params: LWApiPublicNotificationCurrentParamsRawI,
	options?: UseQueryOptions<
		LWApiPublicNotificationCurrentResultRawI,
		ApiErrorI
	>,
) => {
	const { data, ...rest } = useNotificationsCurrent(params, {
		...options,
	});

	const { items } = data || {};

	const notifications = useMemo(
		() =>
			items?.filter(({ last_alert: lastAlert, severity, system, id }) => {
				return (
					// remove id of 0
					id !== 0 &&
					// remove critical
					severity !== 'Critical' &&
					// do not include abort firewall actions
					lastAlert !== 'Activate / deactivate firewall rules Aborted' &&
					// keep provisioning, nocworx workflow, and bare metal
					(isProvisioningSystem(system) ||
						isNocworxWorkflowSystem(system) ||
						isBareMetalSystem(system))
				);
			}),
		[items],
	);

	const latestNotifications = useMemo(() => {
		const toReturn: Record<string, LWApiPublicNotificationDetailsResultRawI> =
			{};

		// get the latest notification for each uniq_id
		notifications?.forEach((notification) => {
			if (!notification.uniq_id) return;

			if (!toReturn[notification.uniq_id]) {
				toReturn[notification.uniq_id] = notification;
			}

			if (
				dayjs(notification.startdate).isAfter(
					dayjs(toReturn[notification.uniq_id].startdate),
				)
			) {
				toReturn[notification.uniq_id] = notification;
			}
		});

		return toReturn;
	}, [notifications]);

	return { ...rest, data: latestNotifications };
};

export default useNotificationsCurrent;
